import { useState, useRef, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import DashboardPage  from './pages/DashboardPage.jsx';
import AgendaPage     from './pages/AgendaPage.jsx';
import VisitaDetalhe  from './pages/VisitaDetalhePage.jsx';
import AgendarPage    from './pages/AgendarPage.jsx';
import TecnicosPage   from './pages/TecnicosPage.jsx';
import AcoesPage      from './pages/AcoesPage.jsx';
import TecnicoEditarPage from './pages/TecnicoEditarPage.jsx';
import EscalasPage     from './pages/EscalasPage.jsx';
import AdminShell             from './pages/admin/AdminShell.jsx';
import AdminHomePage          from './pages/admin/AdminHomePage.jsx';
import AdminUsuariosPage      from './pages/admin/AdminUsuariosPage.jsx';
import AdminFrotaPage         from './pages/admin/AdminFrotaPage.jsx';
import AdminBrandingPage      from './pages/admin/AdminBrandingPage.jsx';
import AdminKpisBackofficePage from './pages/admin/AdminKpisBackofficePage.jsx';
import AdminKpisPontualidadePage from './pages/admin/AdminKpisPontualidadePage.jsx';
import LoginPage      from './pages/LoginPage.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import LogoMais       from './components/LogoMais.jsx';
import Avatar         from './components/Avatar.jsx';
import SinetaNotificacoes from './components/SinetaNotificacoes.jsx';
import { getAuth, clearAuth } from './lib/auth.js';

// Mapeamento de rota → título de cada tela (subtítulo no header)
const TITULOS = {
  '/':         { titulo: 'Centro de Operações', sub: 'Mapa ao vivo' },
  '/agenda':   { titulo: 'Centro de Operações', sub: 'Agenda Inteligente de Campo' },
  '/agendar':  { titulo: 'Centro de Operações', sub: 'Novo agendamento' },
  '/tecnicos': { titulo: 'Centro de Operações', sub: 'Gestão de técnicos' },
  '/escalas':  { titulo: 'Centro de Operações', sub: 'Escalas, feriados e plantão diário' },
  '/feriados': { titulo: 'Centro de Operações', sub: 'Escalas, feriados e plantão diário' },
  '/acoes':    { titulo: 'Centro de Operações', sub: 'Ações sugeridas pela IA' },
};

function tituloPorRota(pathname) {
  if (pathname.startsWith('/tecnicos/')) return { titulo: 'Centro de Operações', sub: 'Editar técnico' };
  if (pathname.startsWith('/visitas/'))  return { titulo: 'Centro de Operações', sub: 'Detalhe da visita' };
  return TITULOS[pathname] || { titulo: 'Centro de Operações', sub: '' };
}

function AppShell({ children }) {
  const auth = getAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuAberto, setMenuAberto] = useState(false);
  const userRef = useRef(null);

  // Fecha menu ao clicar fora
  useEffect(() => {
    function onClick(e) {
      if (userRef.current && !userRef.current.contains(e.target)) {
        setMenuAberto(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function sair() {
    clearAuth();
    nav('/login', { replace: true });
  }

  const { titulo, sub } = tituloPorRota(loc.pathname);
  const userNome = auth?.operador?.nome_completo || auth?.operador?.username || 'Usuário';
  const userRole = auth?.operador?.papel === 'admin' ? 'Administrador' : (auth?.operador?.papel || 'Operador');

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand">
          <LogoMais size={40} mostrarTexto={true} />
        </div>

        <div className="titulo">
          <h1>{titulo}</h1>
          {sub && <p>{sub}</p>}
        </div>

        <nav className="app-nav">
          <NavLink to="/"          end>🗺️ Mapa</NavLink>
          <NavLink to="/agenda"      >📅 Agenda</NavLink>
          <NavLink to="/agendar"     >➕ Novo</NavLink>
          <NavLink to="/tecnicos"    >👥 Técnicos</NavLink>
          <NavLink to="/acoes"       >✨ Ações IA</NavLink>
          {auth?.operador?.papel === 'admin' && (
            <NavLink to="/admin" className="admin-link-nav">⚙️ Admin</NavLink>
          )}
        </nav>

        <SinetaNotificacoes/>

        <div ref={userRef} style={{ position: 'relative' }}>
          <div className="user-card" onClick={() => setMenuAberto(v => !v)}>
            <Avatar nome={userNome} id={auth?.operador?.id || 0} size={36}/>
            <div>
              <div className="nome">{userNome}</div>
              <div className="role">{userRole}</div>
            </div>
            <span style={{ color: '#6b7280', fontSize: 11, marginLeft: 4 }}>▼</span>
          </div>
          {menuAberto && (
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)',
              background: '#fff', border: '1px solid var(--cinza-borda)',
              borderRadius: 10, boxShadow: 'var(--shadow-md)',
              minWidth: 180, zIndex: 200,
              overflow: 'hidden',
            }}>
              <button onClick={sair} style={{
                width: '100%', textAlign: 'left',
                padding: '10px 14px', background: 'transparent',
                border: 'none', cursor: 'pointer',
                fontSize: 13, color: '#374151',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                🚪 Sair
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="app-main" style={{ padding: '20px 24px', maxWidth: 1640, margin: '0 auto' }}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route path="/"            element={<ProtectedRoute><AppShell><DashboardPage /></AppShell></ProtectedRoute>} />
      <Route path="/agenda"      element={<ProtectedRoute><AppShell><AgendaPage /></AppShell></ProtectedRoute>} />
      <Route path="/agendar"     element={<ProtectedRoute><AppShell><AgendarPage /></AppShell></ProtectedRoute>} />
      <Route path="/visitas/:id" element={<ProtectedRoute><AppShell><VisitaDetalhe /></AppShell></ProtectedRoute>} />
      <Route path="/tecnicos"    element={<ProtectedRoute><AppShell><TecnicosPage /></AppShell></ProtectedRoute>} />
      <Route path="/tecnicos/escalas" element={<ProtectedRoute><AppShell><EscalasPage /></AppShell></ProtectedRoute>} />
      <Route path="/tecnicos/:id" element={<ProtectedRoute><AppShell><TecnicoEditarPage /></AppShell></ProtectedRoute>} />
      <Route path="/escalas"     element={<ProtectedRoute><AppShell><EscalasPage /></AppShell></ProtectedRoute>} />
      <Route path="/feriados"    element={<ProtectedRoute><AppShell><EscalasPage /></AppShell></ProtectedRoute>} />
      <Route path="/acoes"       element={<ProtectedRoute><AppShell><AcoesPage /></AppShell></ProtectedRoute>} />

      {/* PAINEL ADMIN — layout próprio, sem AppShell */}
      <Route path="/admin" element={<ProtectedRoute><AdminShell /></ProtectedRoute>}>
        <Route index                  element={<AdminHomePage />} />
        <Route path="usuarios"        element={<AdminUsuariosPage />} />
        <Route path="frota"           element={<AdminFrotaPage />} />
        <Route path="branding"        element={<AdminBrandingPage />} />
        <Route path="kpis/backoffice" element={<AdminKpisBackofficePage />} />
        <Route path="kpis/pontualidade" element={<AdminKpisPontualidadePage />} />
      </Route>
    </Routes>
  );
}
